import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Package, DollarSign, Tag, Megaphone, ShoppingBag, Settings, BarChart3,
  Shield, Key, Users, Loader2, Check, KeyRound, MailPlus, Eye, Mail, FileText, TrendingUp,
} from "lucide-react";

export function SubAdminDialog({
  open, onOpenChange, editingSubAdmin, subAdminForm, setSubAdminForm,
  allPermissions, saving, onSave,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            {editingSubAdmin ? "Edit Sub-Admin Permissions" : "Invite New Sub-Admin"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {!editingSubAdmin && (
            <>
              <div className="space-y-2">
                <Label>Email <span className="text-red-500">*</span></Label>
                <Input
                  type="email"
                  value={subAdminForm.email}
                  onChange={e => setSubAdminForm(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="subadmin@example.com"
                  className="rounded-xl"
                  data-testid="sub-admin-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Full Name <span className="text-red-500">*</span></Label>
                <Input
                  value={subAdminForm.name}
                  onChange={e => setSubAdminForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Full name"
                  className="rounded-xl"
                  data-testid="sub-admin-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Password <span className="text-red-500">*</span></Label>
                <Input
                  type="password"
                  value={subAdminForm.password}
                  onChange={e => setSubAdminForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Set initial password"
                  className="rounded-xl"
                  data-testid="sub-admin-password-input"
                />
              </div>
            </>
          )}
          {editingSubAdmin && (
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                value={subAdminForm.name}
                onChange={e => setSubAdminForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Full name"
                className="rounded-xl"
              />
              <p className="text-xs text-gray-400">Editing: {editingSubAdmin.email}</p>
            </div>
          )}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Permissions</Label>
            <p className="text-sm text-gray-500 -mt-1">Select which sections this sub-admin can access. Toggle "View Only" to restrict editing.</p>
            <div className="grid grid-cols-1 gap-2 border rounded-xl p-4 bg-gray-50/50">
              {allPermissions.map(perm => {
                const labels = {
                  manage_products: { label: "Products", desc: "Create, edit, delete products", icon: Package },
                  manage_orders: { label: "Orders", desc: "View and manage customer orders", icon: DollarSign },
                  manage_promo_codes: { label: "Promo Codes", desc: "Create and manage promotions", icon: Tag },
                  manage_banners: { label: "Banners", desc: "Manage promotional banners", icon: Megaphone },
                  manage_showcase: { label: "Showcase", desc: "Manage showcase products", icon: ShoppingBag },
                  manage_site_settings: { label: "Site Settings", desc: "Edit pages and social links", icon: Settings },
                  view_analytics: { label: "Analytics", desc: "View engagement and reports", icon: BarChart3 },
                  manage_subscribers: { label: "Subscribers", desc: "View newsletter subscribers", icon: MailPlus },
                  manage_users: { label: "Users", desc: "View user sessions and settings", icon: Users },
                  manage_api_keys: { label: "API Keys", desc: "View and update service keys", icon: Key },
                  manage_customer_view: { label: "Customer View", desc: "Manage frontend images and CMS", icon: Eye },
                  manage_email_settings: { label: "Email Settings", desc: "Configure email templates and campaigns", icon: Mail },
                  manage_sample_mocks: { label: "Sample Mocks", desc: "Manage mock exams and questions", icon: FileText },
                  view_engagement: { label: "Engagement", desc: "View conversion and engagement data", icon: TrendingUp },
                };
                const info = labels[perm] || { label: perm, desc: "", icon: Shield };
                const PermIcon = info.icon;
                const isChecked = subAdminForm.permissions.includes(perm);
                const isVO = (subAdminForm.view_only_permissions || []).includes(perm);
                return (
                  <div
                    key={perm}
                    className={`flex items-center gap-3 p-3 rounded-xl transition-all ${isChecked ? "bg-primary/5 border border-primary/20" : "bg-white border border-transparent hover:bg-gray-50"}`}
                    data-testid={`perm-toggle-${perm}`}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={(checked) => {
                        setSubAdminForm(prev => ({
                          ...prev,
                          permissions: checked
                            ? [...prev.permissions, perm]
                            : prev.permissions.filter(p => p !== perm),
                          view_only_permissions: checked
                            ? (prev.view_only_permissions || [])
                            : (prev.view_only_permissions || []).filter(p => p !== perm)
                        }));
                      }}
                    />
                    <PermIcon className={`w-4 h-4 ${isChecked ? "text-primary" : "text-gray-400"}`} />
                    <div className="flex-1">
                      <span className={`text-sm font-medium ${isChecked ? "text-navy-900" : "text-gray-600"}`}>{info.label}</span>
                      <p className="text-xs text-gray-400">{info.desc}</p>
                    </div>
                    {isChecked && (
                      <button
                        type="button"
                        onClick={() => {
                          setSubAdminForm(prev => ({
                            ...prev,
                            view_only_permissions: isVO
                              ? (prev.view_only_permissions || []).filter(p => p !== perm)
                              : [...(prev.view_only_permissions || []), perm]
                          }));
                        }}
                        className={`text-xs px-2.5 py-1 rounded-full border transition-all font-medium ${isVO ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-gray-100 text-gray-500 border-gray-200 hover:bg-amber-50 hover:text-amber-600 hover:border-amber-200"}`}
                        data-testid={`view-only-toggle-${perm}`}
                      >
                        {isVO ? "View Only" : "Full Access"}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={() => setSubAdminForm(prev => ({ ...prev, permissions: [...allPermissions] }))}>
                Select All
              </Button>
              <Button variant="outline" size="sm" className="rounded-lg text-xs" onClick={() => setSubAdminForm(prev => ({ ...prev, permissions: [], view_only_permissions: [] }))}>
                Clear All
              </Button>
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-base font-semibold flex items-center gap-2"><KeyRound className="w-4 h-4" /> Access Code</Label>
            <p className="text-sm text-gray-500 -mt-1">Optional custom access code. Leave blank to auto-generate.</p>
            <Input
              value={subAdminForm.access_code}
              onChange={e => setSubAdminForm(prev => ({ ...prev, access_code: e.target.value.toUpperCase() }))}
              placeholder="Auto-generated if left blank"
              className="rounded-xl font-mono"
              maxLength={16}
              data-testid="sub-admin-access-code-input"
            />
          </div>
          <div className="flex gap-3 pt-3">
            <Button variant="outline" className="flex-1 rounded-xl" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              className="flex-1 rounded-xl"
              data-testid="save-sub-admin-btn"
              onClick={onSave}
              disabled={saving}
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
              {editingSubAdmin ? "Update Permissions" : "Create Sub-Admin"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
